// src/pages/ReferralsPage.tsx

import { useState } from 'react';
import {
  useGetReferralsQuery,
  useCreateReferralMutation,
  useUpdateReferralMutation,
  useDeleteReferralMutation,
} from '../api/referralsApi';
import { useGetContactsQuery } from '../api/contactsApi';
import { useGetCirclesQuery } from '../api/circlesApi';
import { DeleteConfirmDialog } from '../components/ui/ConfirmDialog';
import type {
  Referral,
  ReferralDirection,
  ReferralReason,
  ReferralLikelihood,
  ReferralStatus,
  Contact,
  Circle,
} from '../types/api';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';

const REASON_LABELS: Record<ReferralReason, string> = {
  showed_interest: 'Showed Interest',
  meets_expressed_needs: 'Meets Expressed Needs',
  may_share_synergy: 'May Share Synergy',
};

const LIKELIHOOD_COLORS: Record<ReferralLikelihood, { bg: string; text: string }> = {
  maybe: { bg: 'bg-blue-100', text: 'text-blue-700' },
  likely: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  very_likely: { bg: 'bg-green-100', text: 'text-green-700' },
};

const STATUS_LABELS: Record<ReferralStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  closed_won: 'Won',
  closed_lost: 'Lost',
};

const STATUS_COLORS: Record<ReferralStatus, { bg: string; text: string }> = {
  open: { bg: 'bg-gray-100', text: 'text-gray-700' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-700' },
  closed_won: { bg: 'bg-green-100', text: 'text-green-700' },
  closed_lost: { bg: 'bg-red-100', text: 'text-red-700' },
};

export default function ReferralsPage() {
  const activeWorkspace = useActiveWorkspace();
  const activeWorkspaceId = activeWorkspace?.workspace.id;

  const [activeTab, setActiveTab] = useState<ReferralDirection>('received');
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReferral, setEditingReferral] = useState<Referral | null>(null);
  const [referralToDelete, setReferralToDelete] = useState<Referral | null>(null);

  const { data: referrals = [], isLoading, error } = useGetReferralsQuery(
    {
      workspaceId: activeWorkspaceId,
      direction: activeTab,
      status: statusFilter || undefined,
      search: searchQuery || undefined,
    },
    { skip: !activeWorkspaceId }
  );

  const { data: contactsResponse } = useGetContactsQuery(
    { workspaceId: activeWorkspaceId },
    { skip: !activeWorkspaceId }
  );
  const contacts: Contact[] = contactsResponse?.contacts || [];

  const { data: circles = [] } = useGetCirclesQuery(
    { workspaceId: activeWorkspaceId },
    { skip: !activeWorkspaceId }
  );

  const [createReferral] = useCreateReferralMutation();
  const [updateReferral] = useUpdateReferralMutation();
  const [deleteReferral] = useDeleteReferralMutation();

  const handleCreateReferral = async (data: CreateReferralFormData) => {
    if (!activeWorkspaceId) return;

    await createReferral({
      workspaceId: activeWorkspaceId,
      direction: data.direction,
      subjectContactId: data.subjectContactId || undefined,
      recipientContactId: data.recipientContactId || undefined,
      recipientCircleId: data.recipientCircleId || undefined,
      reason: data.reason,
      likelihood: data.likelihood,
      status: data.status,
      notes: data.notes || undefined,
    });
    setShowCreateModal(false);
  };

  const handleUpdateReferral = async (id: string, data: CreateReferralFormData) => {
    await updateReferral({
      id,
      data: {
        direction: data.direction,
        subjectContactId: data.subjectContactId || null,
        recipientContactId: data.recipientContactId || null,
        recipientCircleId: data.recipientCircleId || null,
        reason: data.reason,
        likelihood: data.likelihood,
        status: data.status,
        notes: data.notes || null,
      },
    });
    setEditingReferral(null);
  };

  const handleDeleteReferralClick = (referral: Referral) => {
    setReferralToDelete(referral);
  };

  const handleConfirmDeleteReferral = async () => {
    if (!referralToDelete) return;
    await deleteReferral(referralToDelete.id);
    setReferralToDelete(null);
    setEditingReferral(null);
  };

  if (!activeWorkspaceId) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace first.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Error loading referrals. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Referrals</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
        >
          New Referral
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('received')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'received'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Received
        </button>
        <button
          onClick={() => setActiveTab('given')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'given'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Given
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ReferralStatus | '')}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="closed_won">Closed Won</option>
          <option value="closed_lost">Closed Lost</option>
        </select>
        <input
          type="text"
          placeholder="Search notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Referrals List */}
      {referrals.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <p className="text-gray-500">
            No {activeTab} referrals yet. Create your first referral to track leads.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Subject
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Recipient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Reason
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Likelihood
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {referrals.map((referral) => (
                  <tr
                    key={referral.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setEditingReferral(referral)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">
                        {referral.subjectContact?.fullName || '—'}
                      </div>
                      {referral.subjectContact?.company && (
                        <div className="text-gray-500 text-xs">
                          {referral.subjectContact.company}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {referral.recipientContact ? (
                        <div className="font-medium text-gray-900">
                          {referral.recipientContact.fullName}
                        </div>
                      ) : referral.recipientCircle ? (
                        <div className="flex items-center gap-2">
                          {referral.recipientCircle.color && (
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: referral.recipientCircle.color }}
                            />
                          )}
                          <span className="font-medium text-gray-900">
                            {referral.recipientCircle.name}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            Circle
                          </span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {REASON_LABELS[referral.reason]}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          LIKELIHOOD_COLORS[referral.likelihood].bg
                        } ${LIKELIHOOD_COLORS[referral.likelihood].text}`}
                      >
                        {referral.likelihood === 'maybe'
                          ? 'Maybe'
                          : referral.likelihood === 'likely'
                          ? 'Likely'
                          : 'Very Likely'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          STATUS_COLORS[referral.status].bg
                        } ${STATUS_COLORS[referral.status].text}`}
                      >
                        {STATUS_LABELS[referral.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingReferral) && (
        <ReferralModal
          referral={editingReferral}
          contacts={contacts}
          circles={circles}
          defaultDirection={activeTab}
          onClose={() => {
            setShowCreateModal(false);
            setEditingReferral(null);
          }}
          onSave={(data) => {
            if (editingReferral) {
              handleUpdateReferral(editingReferral.id, data);
            } else {
              handleCreateReferral(data);
            }
          }}
          onDelete={editingReferral ? () => handleDeleteReferralClick(editingReferral) : undefined}
        />
      )}

      {/* Delete Referral Confirm Dialog */}
      <DeleteConfirmDialog
        isOpen={!!referralToDelete}
        itemName={referralToDelete?.subjectContact?.fullName || 'this referral'}
        itemType="referral"
        onConfirm={handleConfirmDeleteReferral}
        onCancel={() => setReferralToDelete(null)}
      />
    </div>
  );
}

interface CreateReferralFormData {
  direction: ReferralDirection;
  subjectContactId: string;
  recipientContactId: string;
  recipientCircleId: string;
  reason: ReferralReason;
  likelihood: ReferralLikelihood;
  status: ReferralStatus;
  notes: string;
}

interface ReferralModalProps {
  referral: Referral | null;
  contacts: Contact[];
  circles: Circle[];
  defaultDirection: ReferralDirection;
  onClose: () => void;
  onSave: (data: CreateReferralFormData) => void;
  onDelete?: () => void;
}

function ReferralModal({
  referral,
  contacts,
  circles,
  defaultDirection,
  onClose,
  onSave,
  onDelete,
}: ReferralModalProps) {
  const [direction, setDirection] = useState<ReferralDirection>(
    referral?.direction || defaultDirection
  );
  const [subjectContactId, setSubjectContactId] = useState(
    referral?.subjectContactId || ''
  );
  const [recipientType, setRecipientType] = useState<'contact' | 'circle'>(
    referral?.recipientCircleId ? 'circle' : 'contact'
  );
  const [recipientContactId, setRecipientContactId] = useState(
    referral?.recipientContactId || ''
  );
  const [recipientCircleId, setRecipientCircleId] = useState(
    referral?.recipientCircleId || ''
  );
  const [reason, setReason] = useState<ReferralReason>(
    referral?.reason || 'showed_interest'
  );
  const [likelihood, setLikelihood] = useState<ReferralLikelihood>(
    referral?.likelihood || 'maybe'
  );
  const [status, setStatus] = useState<ReferralStatus>(referral?.status || 'open');
  const [notes, setNotes] = useState(referral?.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!recipientContactId && !recipientCircleId) {
      alert('Please select a recipient');
      return;
    }

    onSave({
      direction,
      subjectContactId,
      recipientContactId: recipientType === 'contact' ? recipientContactId : '',
      recipientCircleId: recipientType === 'circle' ? recipientCircleId : '',
      reason,
      likelihood,
      status,
      notes,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              {referral ? 'Edit Referral' : 'Create Referral'}
            </h2>
          </div>

          <div className="p-6 space-y-4">
            {/* Direction */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Direction
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="direction"
                    value="given"
                    checked={direction === 'given'}
                    onChange={() => setDirection('given')}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm">Given</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="direction"
                    value="received"
                    checked={direction === 'received'}
                    onChange={() => setDirection('received')}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm">Received</span>
                </label>
              </div>
            </div>

            {/* Subject Contact */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject (who is being referred)
              </label>
              <select
                value={subjectContactId}
                onChange={(e) => setSubjectContactId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a contact...</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.fullName}
                    {contact.company ? ` (${contact.company})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Recipient Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="recipientType"
                    value="contact"
                    checked={recipientType === 'contact'}
                    onChange={() => {
                      setRecipientType('contact');
                      setRecipientCircleId('');
                    }}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm">Contact</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="recipientType"
                    value="circle"
                    checked={recipientType === 'circle'}
                    onChange={() => {
                      setRecipientType('circle');
                      setRecipientContactId('');
                    }}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm">Circle</span>
                </label>
              </div>
            </div>

            {/* Recipient Selection */}
            {recipientType === 'contact' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Contact *
                </label>
                <select
                  value={recipientContactId}
                  onChange={(e) => setRecipientContactId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select a contact...</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.fullName}
                      {contact.company ? ` (${contact.company})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Circle *
                </label>
                <select
                  value={recipientCircleId}
                  onChange={(e) => setRecipientCircleId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select a circle...</option>
                  {circles.map((circle) => (
                    <option key={circle.id} value={circle.id}>
                      {circle.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason *
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value as ReferralReason)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="showed_interest">Showed Interest</option>
                <option value="meets_expressed_needs">Meets Expressed Needs</option>
                <option value="may_share_synergy">May Share Synergy</option>
              </select>
            </div>

            {/* Likelihood */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Likelihood to Close *
              </label>
              <div className="flex gap-2">
                {(['maybe', 'likely', 'very_likely'] as ReferralLikelihood[]).map(
                  (opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setLikelihood(opt)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        likelihood === opt
                          ? `${LIKELIHOOD_COLORS[opt].bg} ${LIKELIHOOD_COLORS[opt].text} border-transparent`
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {opt === 'maybe'
                        ? 'Maybe'
                        : opt === 'likely'
                        ? 'Likely'
                        : 'Very Likely'}
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ReferralStatus)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="closed_won">Closed Won</option>
                <option value="closed_lost">Closed Lost</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Optional notes..."
              />
            </div>
          </div>

          <div className="p-6 border-t flex justify-between">
            <div>
              {onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="px-4 py-2 text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  Delete
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
              >
                {referral ? 'Save Changes' : 'Create Referral'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
